import React, { useState, useRef, useEffect } from "react";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import {
  AttachFile as AttachFileIcon,
  ArrowUpward as ArrowUpwardIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Mic as MicIcon,
  Stop as StopIcon,
} from "@mui/icons-material";
import { colors } from "../theme/colors";
import { FilePreview } from "./CustomFilePreview";
import { VoiceWaveform } from "./VoiceWaveform";
import CustomLoading from "../Loading/CustomLoading";
import type { FileAttachment } from "./AIChatTypes";
import { useVoiceRecorder } from "./useVoiceRecorder";
import { transcribeAudio } from "./whisperApi";
import { messages } from "./messages";

interface ChatInputProps {
  onSendMessage: (content: string, files: FileAttachment[]) => void;
  onCancelSend?: () => void;
  primaryColor?: string;
  isSending?: boolean;
  isExpanded?: boolean;
  backendUrl?: string;
  // 未指定時は共通の placeholder(messages.ui.placeholder) を使う。
  placeholder?: string;
}

const shouldSuppressTranscriptionWarning = (error: any): boolean => {
  const message =
    typeof error?.message === "string" ? error.message.toLowerCase() : "";
  const status = typeof error?.status === "number" ? error.status : undefined;

  // 音声文字起こしは補助機能。認証系エラーはUIノイズになるため非表示にする。
  return (
    status === 401 ||
    message.includes("認証トークンが必要です") ||
    message.includes("authentication required") ||
    message.includes("unauthorized")
  );
};

export const ChatInput: React.FC<ChatInputProps> = ({
  onSendMessage,
  onCancelSend,
  primaryColor: _primaryColor = colors.primary.main,
  isSending = false,
  isExpanded = false,
  backendUrl,
  placeholder,
}) => {
  const errorFadeOutMs = 200;
  const shortRecordingErrorMessage = messages.errors.shortRecording;
  const [message, setMessage] = useState("");
  const [files, setFiles] = useState<FileAttachment[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isVoiceActivationRequested, setIsVoiceActivationRequested] =
    useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [transcriptionError, setTranscriptionError] = useState<string | null>(
    null
  );
  const [hideRecorderError, setHideRecorderError] = useState(false);
  const [isErrorFadingOut, setIsErrorFadingOut] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const isProcessingRef = useRef<boolean>(false);

  const {
    isRecording,
    isStartingRecording,
    recordingTime,
    startRecording,
    stopRecording,
    error: recorderError,
    hasPermission,
  } = useVoiceRecorder();
  const isVoicePreparing =
    isVoiceActivationRequested || isStartingRecording;
  const isSendDisabled =
    isSending ||
    isVoicePreparing ||
    isTranscribing ||
    (!isRecording && !message.trim() && files.length === 0);

  useEffect(() => {
    setHideRecorderError(false);
  }, [recorderError]);

  const SUPPORTED_TYPES = [
    "application/pdf",
    "image/png",
    "image/jpeg",
    "image/gif",
    "image/webp",
  ];

  const getFileTypeLabel = (mimeType: string): string => {
    if (mimeType === "application/pdf") return "PDF";
    if (mimeType.startsWith("image/")) return "IMAGE";
    return "FILE";
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    const supportedFiles = selectedFiles.filter((file) =>
      SUPPORTED_TYPES.includes(file.type)
    );
    const newFiles: FileAttachment[] = supportedFiles.map((file) => ({
      id: Date.now().toString() + Math.random(),
      name: file.name,
      type: getFileTypeLabel(file.type),
      file: file,
    }));
    setFiles([...files, ...newFiles]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemoveFile = (fileId: string) => {
    setFiles(files.filter((f) => f.id !== fileId));
  };

  // 入力欄は常に 2 行レイアウト（テキスト上段 + クリップ/マイク/送信の下段ツールバー）にする。
  // 単一行↔複数行の切り替えで先頭位置や高さが動くのを防ぎ、一定の見た目にする。
  const isMultiLine = true;
  const hiddenTextRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      const scrollHeight = textareaRef.current.scrollHeight;
      const maxHeight = 120;
      textareaRef.current.style.height = `${Math.min(scrollHeight, maxHeight)}px`;
    }
  }, [message]);

  const dismissErrorWithFade = (target: "transcription" | "recorder") => {
    if (isErrorFadingOut) {
      return;
    }

    setIsErrorFadingOut(true);
    setTimeout(() => {
      if (target === "transcription") {
        setTranscriptionError(null);
      } else {
        setHideRecorderError(true);
      }
      setIsErrorFadingOut(false);
    }, errorFadeOutMs);
  };

  useEffect(() => {
    if (transcriptionError !== shortRecordingErrorMessage) {
      return;
    }

    const timer = setTimeout(() => {
      dismissErrorWithFade("transcription");
    }, 3000);

    return () => clearTimeout(timer);
  }, [transcriptionError, shortRecordingErrorMessage]);

  const handleSend = () => {
    if (!isSendDisabled && !isRecording) {
      const contentToSend = message;
      const filesToSend = [...files];

      // 即座にクリア（楽観的UI更新）
      setMessage("");
      setFiles([]);

      // textareaをクリア
      if (textareaRef.current) {
        textareaRef.current.value = "";
      }

      // 送信処理を実行
      onSendMessage(contentToSend, filesToSend);

      // フォーカスを戻す
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
        }
      }, 100);
    }
  };

  const handleCancelSend = () => {
    if (!isSending) return;
    onCancelSend?.();
  };

  const handleVoiceInput = async () => {
    if (isProcessingRef.current) {
      return;
    }

    if (isRecording) {
      if (recordingTime < 1) {
        setTranscriptionError(shortRecordingErrorMessage);
        isProcessingRef.current = true;
        try {
          await stopRecording();
        } catch (error) {
          console.error("録音停止エラー:", error);
        } finally {
          isProcessingRef.current = false;
        }
        return;
      }

      setTranscriptionError(null);
      setIsTranscribing(true);
      isProcessingRef.current = true;
      try {
        const audioBlob = await stopRecording();
        if (!audioBlob) {
          throw new Error(messages.errors.recordingDataNotFound);
        }

        if (audioBlob.size === 0) {
          throw new Error(messages.errors.recordingDataEmpty);
        }

        if (audioBlob.size < 1000) {
          throw new Error(messages.errors.recordingDataTooSmall);
        }

        const getBackendUrl = (): string => {
          if (backendUrl) {
            return backendUrl;
          }
          if (
            typeof import.meta !== "undefined" &&
            import.meta.env?.VITE_API_BASE_URL
          ) {
            return `${import.meta.env.VITE_API_BASE_URL}/chat/transcribe`;
          }
          if (
            typeof import.meta !== "undefined" &&
            import.meta.env?.VITE_API_URL
          ) {
            return `${import.meta.env.VITE_API_URL}/api/v1/chat/transcribe`;
          }
          throw new Error(messages.errors.backendUrlNotSet);
        };

        const transcribedText = await transcribeAudio(audioBlob, {
          backendUrl: getBackendUrl(),
        });
        if (transcribedText) {
          setMessage((prev) =>
            prev ? `${prev} ${transcribedText}` : transcribedText
          );
          setTimeout(() => {
            if (textareaRef.current) {
              textareaRef.current.focus();
            }
          }, 100);
        } else {
          throw new Error(messages.errors.transcriptionEmpty);
        }
      } catch (error: any) {
        console.error("Transcription error:", error);
        if (shouldSuppressTranscriptionWarning(error)) {
          setTranscriptionError(null);
          return;
        }
        const errorMessage =
          error.message || messages.errors.transcriptionFailed;
        setTranscriptionError(errorMessage);
      } finally {
        setIsTranscribing(false);
        isProcessingRef.current = false;
      }
    } else {
      setTranscriptionError(null);
      isProcessingRef.current = true;
      setIsVoiceActivationRequested(true);
      try {
        await startRecording();
      } catch (error) {
        console.error("録音開始エラー:", error);
        setTranscriptionError(messages.errors.recordingStartFailed);
      } finally {
        setIsVoiceActivationRequested(false);
        isProcessingRef.current = false;
      }
    }
  };

  const handleCancelRecording = async () => {
    if (!isRecording || isProcessingRef.current) return;

    isProcessingRef.current = true;
    setTranscriptionError(null);
    try {
      await stopRecording();
    } catch (error) {
      console.error("録音キャンセルエラー:", error);
    } finally {
      isProcessingRef.current = false;
    }
  };

  // ドラッグ&ドロップハンドラー
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    const supportedFiles = droppedFiles.filter((file) =>
      SUPPORTED_TYPES.includes(file.type)
    );
    const newFiles: FileAttachment[] = supportedFiles.map((file) => ({
      id: Date.now().toString() + Math.random(),
      name: file.name,
      type: getFileTypeLabel(file.type),
      file: file,
    }));
    setFiles([...files, ...newFiles]);
  };

  return (
    <Box
      sx={{
        backgroundColor: "#FFFFFF",
      }}
    >
      {/* エラーメッセージ表示 */}
      {(transcriptionError || (!hideRecorderError && recorderError)) && (
        <Box
          sx={{
            mb: 1.5,
            p: 1.5,
            mx: "auto",
            maxWidth: "56rem",
            boxSizing: "border-box",
            backgroundColor: "#FFEBEE",
            borderRadius: "8px",
            border: "1px solid #FFCDD2",
            position: "relative",
            opacity: isErrorFadingOut ? 0 : 1,
            transition: `opacity ${errorFadeOutMs}ms ease`,
          }}
        >
          <IconButton
            onClick={() => {
              if (transcriptionError) {
                dismissErrorWithFade("transcription");
              } else {
                dismissErrorWithFade("recorder");
              }
            }}
            size="small"
            sx={{
              position: "absolute",
              top: 6,
              right: 6,
              color: "#C62828",
            }}
            aria-label={messages.aria.closeErrorButton}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <Typography
            sx={{
              fontSize: 13,
              color: "#C62828",
              // 右上の閉じる [×] ボタンと重ならないよう pr を確保
              pr: 3.5,
            }}
          >
            {transcriptionError || (hideRecorderError ? null : recorderError)}
          </Typography>
        </Box>
      )}

      {/* 入力フィールド */}
      <Box
        sx={{
          position: "relative",
          border: "1px solid #E0E0E0",
          borderRadius: "28px",
          backgroundColor: "#FFFFFF",
          mx: "auto",
          maxWidth: "56rem",
          minHeight: "56px",
          // 上の余白だけ広めに（16px）、下は 8px。
          pt: 2,
          pb: 1,
          px: files.length > 0 ? 1 : isMultiLine ? 1 : 0,
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
          display: "flex",
          flexDirection: "column",
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {/* ファイルプレビュー - 入力枠の中、テキストの上に表示 */}
        {files.length > 0 && (
          <Box sx={{ px: 1.5, pt: 0.5, pb: 0.75 }}>
            <Box
              sx={{
                display: "flex",
                flexWrap: "nowrap",
                gap: 1.5,
                overflowX: "auto",
                overflowY: "hidden",
                WebkitOverflowScrolling: "touch",
                pr: 1,
                "&::-webkit-scrollbar": {
                  height: 6,
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "rgba(0, 0, 0, 0.15)",
                  borderRadius: 999,
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "transparent",
                },
              }}
            >
              {files.map((file) => (
                <Box
                  key={file.id}
                  sx={{
                    // 各ファイルは自身のサイズ (画像サムネイルは 50x50 px、
                    // compact 行は中身の幅) を取る。旧実装は `calc(100% / N)` で
                    // 均等分割していたが、ファイル数が少ない時に幅が広がりすぎて
                    // スカスカに見えるため撤廃。
                    flex: "0 0 auto",
                  }}
                >
                  <FilePreview
                    file={file}
                    onRemove={() => handleRemoveFile(file.id)}
                    primaryColor={_primaryColor}
                    showImagePreview={isExpanded}
                    variant={isExpanded ? "imageCard" : "compact"}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        )}

        <Box
          sx={{
            position: "relative",
            display: isMultiLine ? "grid" : "flex",
            gridTemplateColumns: isMultiLine ? "auto 1fr auto" : "none",
            gridTemplateRows: isMultiLine ? "auto 1fr" : "none",
            alignItems: isMultiLine ? "stretch" : "center",
            width: "100%",
          }}
        >
          {/* 高さ測定用の隠し要素 */}
          <Box
            ref={hiddenTextRef}
            sx={{
              position: "absolute",
              visibility: "hidden",
              height: "auto",
              width: "calc(100% - 130px)",
              fontSize: 15,
              lineHeight: 1.5,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              pointerEvents: "none",
              zIndex: -1,
              top: 0,
              left: 50,
            }}
          >
            {message + "\u200b"}
          </Box>

          {/* ドラッグオーバーレイ */}
          {isDragging && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                backgroundColor: "rgba(11, 157, 189, 0.05)",
                border: `2px dashed ${colors.primary.main}`,
                borderRadius: "26px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 10,
                pointerEvents: "none",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  color: colors.primary.main,
                  fontSize: 14,
                }}
              >
                <AttachFileIcon />
                {messages.ui.dragAndDrop}
              </Box>
            </Box>
          )}

          {/* クリップアイコン（左下） */}
          <IconButton
            onClick={() => fileInputRef.current?.click()}
            size="small"
            sx={{
              gridColumn: isMultiLine ? "1" : "auto",
              gridRow: isMultiLine ? "2" : "auto",
              ml: isMultiLine ? 0 : 1,
              p: 1,
              color: "#000000",
              borderRadius: "50%",
              alignSelf: isMultiLine ? "flex-end" : "center",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.06)",
              },
            }}
            aria-label={messages.aria.fileInput}
          >
            <AttachFileIcon sx={{ fontSize: 20 }} />
          </IconButton>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="application/pdf,image/png,image/jpeg,image/gif,image/webp"
            style={{ display: "none" }}
            onChange={handleFileSelect}
          />

          {/* 録音中: 入力の中央に音声波形を表示（textarea は視覚的に隠す） */}
          {isRecording && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                pointerEvents: "none",
                zIndex: 6,
              }}
            >
              <VoiceWaveform active={isRecording} width={120} height={28} />
            </Box>
          )}

          {/* 録音準備・文字起こし中: 入力の中央に小さいローディングを表示 */}
          {(isVoicePreparing || isTranscribing) && !isRecording && (
            <Box
              role="status"
              aria-label={
                isVoicePreparing
                  ? messages.aria.micButton.preparing
                  : messages.aria.micButton.transcribing
              }
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                pointerEvents: "none",
                zIndex: 6,
              }}
            >
              <CustomLoading
                loading
                fullScreen={false}
                variant="circular"
                size="small"
              />
            </Box>
          )}

          {/* テキスト入力エリア（左上） */}
          <Box
            component="textarea"
            ref={textareaRef}
            rows={1}
            value={message}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              setMessage(e.target.value)
            }
            placeholder={
              isSending
                ? messages.ui.sending
                : (placeholder ?? messages.ui.placeholder)
            }
            disabled={isSending || isVoicePreparing}
            onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
              // IME変換中（日本語入力の確定Enterなど）は送信しない
              if (e.nativeEvent.isComposing || e.keyCode === 229) return;
              if (
                e.key === "Enter" &&
                !e.shiftKey &&
                !isRecording &&
                !isSendDisabled
              ) {
                e.preventDefault();
                handleSend();
              }
            }}
            sx={{
              gridColumn: isMultiLine ? "1 / 4" : "auto",
              gridRow: isMultiLine ? "1" : "auto",
              flex: isMultiLine ? "none" : 1,
              // 複数行時はクリップアイコンの左に軽く合わせる程度の小さな左パディング。
              pl: isMultiLine ? "10px" : 0,
              border: "none",
              outline: "none",
              resize: "none",
              fontSize: 15,
              lineHeight: 1.5,
              color: colors.text.primary,
              fontFamily: "'Noto Sans JP', sans-serif",
              backgroundColor: "transparent",
              // 録音中は波形、準備・文字起こし中はローディングを見せるため textarea を隠す
              opacity:
                isRecording || isVoicePreparing || isTranscribing
                  ? 0
                  : isSending
                    ? 0.6
                    : 1,
              caretColor:
                isRecording || isVoicePreparing || isTranscribing
                  ? "transparent"
                  : undefined,
              cursor:
                isSending || isVoicePreparing ? "not-allowed" : "text",
              minHeight: "24px",
              maxHeight: "120px",
              overflowY: "auto",
              marginTop: isMultiLine ? "0" : "16px",
              paddingBottom: isMultiLine ? "0" : "16px",
              width: isMultiLine ? "100%" : "auto",
              alignSelf: isMultiLine ? "start" : "center",
              "&::placeholder": {
                color: "#9E9E9E",
              },
              "&:disabled": {
                color: colors.text.primary,
              },
            }}
          />

          {/* マイクアイコンと送信ボタン（右下） */}
          <Box
            sx={{
              gridColumn: isMultiLine ? "3" : "auto",
              gridRow: isMultiLine ? "2" : "auto",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              alignSelf: isMultiLine ? "flex-end" : "center",
            }}
          >
            <IconButton
              onClick={isRecording ? handleCancelRecording : handleVoiceInput}
              disabled={
                isSending ||
                isVoicePreparing ||
                isTranscribing ||
                hasPermission === false
              }
              size="small"
              sx={{
                p: 1,
                color: isRecording ? "#F44336" : "#000000",
                borderRadius: "50%",
                backgroundColor: isRecording
                  ? "rgba(244, 67, 54, 0.1)"
                  : "transparent",
                animation: isRecording
                  ? "pulse 1.5s ease-in-out infinite"
                  : "none",
                "@keyframes pulse": {
                  "0%, 100%": {
                    transform: "scale(1)",
                  },
                  "50%": {
                    transform: "scale(1.1)",
                  },
                },
                "&:hover": {
                  backgroundColor: isRecording
                    ? "rgba(244, 67, 54, 0.15)"
                    : "rgba(0, 0, 0, 0.06)",
                },
                "&.Mui-disabled": {
                  color: "#9E9E9E",
                  backgroundColor: "transparent",
                },
              }}
              title={
                hasPermission === false
                  ? messages.ui.micPermissionRequired
                  : isRecording
                    ? messages.ui.cancelRecording
                    : isVoicePreparing
                      ? messages.ui.preparingVoiceInput
                      : isTranscribing
                      ? messages.ui.transcribing
                      : messages.ui.voiceInput
              }
              aria-label={
                hasPermission === false
                  ? messages.aria.micButton.permissionDenied
                  : isRecording
                    ? messages.aria.micButton.recording
                    : isVoicePreparing
                      ? messages.aria.micButton.preparing
                      : isTranscribing
                      ? messages.aria.micButton.transcribing
                      : isSending || isVoicePreparing || isTranscribing
                        ? messages.aria.micButton.disabled
                        : messages.aria.micButton.stopped
              }
              aria-live="polite"
              aria-atomic="true"
            >
              {isRecording ? (
                <CloseIcon sx={{ fontSize: 20 }} />
              ) : (
                <MicIcon sx={{ fontSize: 20 }} />
              )}
            </IconButton>

            <IconButton
              onClick={isRecording ? handleVoiceInput : handleSend}
              disabled={isSendDisabled}
              sx={{
                width: 32,
                height: 32,
                minWidth: 32,
                backgroundColor: colors.primary.active,
                color: "#FFFFFF",
                borderRadius: "50%",
                "&:hover": {
                  backgroundColor: colors.primary.main,
                },
                "&.Mui-disabled": {
                  backgroundColor: "#D0D0D0",
                  color: "#FFFFFF",
                },
              }}
              title={
                isRecording
                  ? messages.ui.confirmRecording
                  : isSending
                    ? messages.ui.sending
                    : messages.ui.send
              }
              aria-label={
                isRecording
                  ? messages.aria.sendButton.confirmRecording
                  : isSending
                  ? messages.aria.sendButton.sending
                  : isSendDisabled
                    ? messages.aria.sendButton.disabled
                    : messages.aria.sendButton.enabled
              }
              aria-live="polite"
              aria-atomic="true"
            >
              {isRecording ? (
                <CheckIcon sx={{ fontSize: 18 }} />
              ) : (
                <ArrowUpwardIcon sx={{ fontSize: 18 }} />
              )}
            </IconButton>
            {isSending && (
              <Tooltip
                title={messages.ui.cancelSend}
                placement="top"
                arrow
                slotProps={{
                  popper: {
                    sx: {
                      "& .MuiTooltip-tooltip": {
                        backgroundColor: colors.chatCancelButton.tooltip.background,
                        color: colors.chatCancelButton.tooltip.text,
                      },
                      "& .MuiTooltip-arrow": {
                        color: colors.chatCancelButton.tooltip.background,
                      },
                    },
                  },
                }}
              >
                <span>
                  <IconButton
                    onClick={handleCancelSend}
                    sx={{
                      width: 32,
                      height: 32,
                      minWidth: 32,
                      color: colors.chatCancelButton.icon,
                      backgroundColor: colors.chatCancelButton.background,
                      borderRadius: "50%",
                      border: `1px solid ${colors.chatCancelButton.border}`,
                      "&:hover": {
                        backgroundColor: colors.chatCancelButton.hover.background,
                        opacity: colors.chatCancelButton.hover.opacity,
                      },
                    }}
                    aria-label={messages.aria.cancelButton}
                  >
                    <StopIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
